import { useState, useEffect } from "react";
import { useRouter, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Home,
  HandHeart,
  Stethoscope,
  Receipt,
  MessageSquareHeart,
  LogOut,
  UserSearch,
  UserPlus,
  Send,
  BriefcaseMedical,
  KeyRound,
  FileText,
  ShieldCheck,
} from "lucide-react";

import { ELocale } from "@/app/types/locale";
import { ParagraphBold } from "@/components/ui/typography/ParagraphBold";
import { SidebarItem } from "./SidebarItem";
import { SidebarLinkItem } from "./SidebarLinkItem";
import { AccordionSidebarItem } from "./AccordionSidebarItem";

const routes: Record<string, string> = {
  "/dashboard/profile/delete-account": "DeleteAccount",
  "/dashboard/profile/change-password": "ChangePassword",
  "/dashboard/profile": "Profile",
  "/dashboard/patients/new": "New",
  "/dashboard/patients/invites": "Invite",
  "/dashboard/patients": "Patients",
  "/dashboard/billing": "Billing",
  "/dashboard/help": "Help",
  "/dashboard/support": "Support",
  "/dashboard": "Home",
};

const Sidebar: React.FC = () => {
  const router = useRouter();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const websiteLocale =
    i18n.language === ELocale.ES
      ? ELocale.ES
      : i18n.language === ELocale.CA
        ? ELocale.CA
        : ELocale.EN;

  const [activeItem, setActiveItem] = useState<string>("Home");
  const isProfileSection = location.pathname.startsWith("/dashboard/profile");
  const isPatientsSection = location.pathname.startsWith("/dashboard/patients");
  const [isProfileOpen, setIsProfileOpen] = useState(isProfileSection);
  const [isPatientsOpen, setIsPatientsOpen] = useState(isPatientsSection);

  useEffect(() => {
    const path = location.pathname;
    const activeItem = Object.keys(routes).find((route) =>
      path.includes(route)
    );
    setActiveItem(routes[activeItem || "Home"]);

    if (isProfileSection) {
      setIsProfileOpen(true);
    }

    if (isPatientsSection) {
      setIsPatientsOpen(true);
    }
  }, [isPatientsSection, isProfileSection, location.pathname]);

  const handleLogout = () => {
    sessionStorage.clear();
    router.navigate({ to: "/login" });
  };

  return (
    <aside className="min-w-72 max-w-fit min-h-full border-r border-neutral-100 p-4 overflow-y-scroll [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-beige [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-neutral-100">
      <div className="flex flex-col justify-between min-h-full">
        <nav className="space-y-2">
          <SidebarItem
            Icon={Home}
            label={t("general.sidebar.home")}
            active={activeItem === "Home"}
            onClick={() => {
              router.navigate({ to: "/dashboard" });
            }}
          />

          {/* Profile Accordion options */}

          <AccordionSidebarItem
            Icon={BriefcaseMedical}
            label={t("general.sidebar.profile_accordion")}
            open={isProfileOpen}
            onOpenChange={setIsProfileOpen}
          >
            <SidebarItem
              Icon={Stethoscope}
              label={t("general.sidebar.profile")}
              active={activeItem === "Profile"}
              onClick={() => {
                router.navigate({ to: "/dashboard/profile" });
              }}
            />

            <SidebarItem
              Icon={KeyRound}
              label={t("general.sidebar.change_password")}
              active={activeItem === "ChangePassword"}
              onClick={() => {
                router.navigate({ to: "/dashboard/profile/change-password" });
              }}
            />

            {/* <SidebarItem
              Icon={Trash2}
              label={t("general.sidebar.delete_account")}
              active={activeItem === "DeleteAccount"}
              onClick={() => {
                router.navigate({ to: "/dashboard/profile/delete-account" });
              }}
            /> */}
          </AccordionSidebarItem>

          {/* Patients Accordion options */}

          <AccordionSidebarItem
            Icon={HandHeart}
            label={t("general.sidebar.patients_accordion")}
            open={isPatientsOpen}
            onOpenChange={setIsPatientsOpen}
          >
            <>
              <SidebarItem
                Icon={UserSearch}
                label={t("general.sidebar.patients")}
                active={activeItem === "Patients"}
                onClick={() => {
                  router.navigate({ to: "/dashboard/patients" });
                }}
              />
              <SidebarItem
                Icon={UserPlus}
                label={t("general.sidebar.add_new")}
                active={activeItem === "New"}
                onClick={() => {
                  router.navigate({ to: "/dashboard/patients/new" });
                }}
              />
              <SidebarItem
                Icon={Send}
                label={t("general.sidebar.invites")}
                active={activeItem === "Invite"}
                onClick={() => {
                  router.navigate({ to: "/dashboard/patients/invites" });
                }}
              />
            </>
          </AccordionSidebarItem>

          <SidebarItem
            Icon={Receipt}
            label={t("general.sidebar.billing")}
            active={activeItem === "Billing"}
            onClick={() => {
              router.navigate({ to: "/dashboard/billing" });
            }}
          />
          <SidebarItem
            Icon={MessageSquareHeart}
            label={t("general.sidebar.support")}
            active={activeItem === "Support"}
            onClick={() => {
              router.navigate({ to: "/dashboard/support" });
            }}
          />

          <SidebarLinkItem
            Icon={FileText}
            href={`https://www.mimiathealth.com/${websiteLocale}/clinicians/terms/v1`}
            label={t("general.sidebar.terms")}
          />

          <SidebarLinkItem
            Icon={ShieldCheck}
            href={`https://www.mimiathealth.com/${websiteLocale}/clinicians/privacy/v1`}
            label={t("general.sidebar.privacy")}
          />

          {/* <SidebarLinkItem
            Icon={BookOpenCheck}
            href="https://mimiathealth.com"
            label={t("general.sidebar.help")}
          /> */}
        </nav>

        <button
          onClick={handleLogout}
          className="flex items-center space-x-2 p-2 mt-10 cursor-pointer rounded-md hover:bg-red-100"
        >
          <LogOut color="#DD2027" />
          <ParagraphBold color="text-red">
            {t("general.sidebar.logout")}
          </ParagraphBold>
        </button>
      </div>
    </aside>
  );
};

export { Sidebar };
